import type { HttpError } from 'http-errors';

import { ErrorContext } from './api';

/**
 * Details of an error returned from the Jupiter API.
 */
export interface JupiterApiErrorDetails {
  /** Error message */
  error: string;
  /** Error code */
  errorCode: string;
}

/**
 * Parent class to handle Jupiter API errors with details.
 */
export class JupiterApiError extends Error implements Omit<HttpError, 'expose'> {
  public context?: ErrorContext;
  public details: JupiterApiErrorDetails;
  public errorCode: string;
  public status: number;
  public statusCode: number;

  constructor(status: number, details: JupiterApiErrorDetails, context?: ErrorContext) {
    super(details.error);
    this.name = 'JupiterApiError';
    this.details = details;
    this.status = status;
    this.statusCode = status;
    this.errorCode = details.errorCode;

    // optional context
    if (context) this.context = context;

    // Set the prototype explicitly to maintain instanceof checks when
    // targeting older JavaScript runtimes.
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * `TOKEN_NOT_TRADABLE` errors.
 */
export class JupiterTokenNotTradableError extends JupiterApiError {
  public name = 'JupiterTokenNotTradableError';

  constructor(details: JupiterApiErrorDetails, context?: ErrorContext) {
    super(422, details, context);
  }
}

/**
 * `FETCH_ROUTES_ERROR`, `NO_ROUTES_ERROR`, `COULD_NOT_FIND_ANY_ROUTE` errors.
 */
export class JupiterFetchRoutesError extends JupiterApiError {
  public name = 'JupiterFetchRoutesError';

  constructor(details: JupiterApiErrorDetails, context?: ErrorContext) {
    super(422, details, context);
  }
}

/**
 * `ROUTE_PLAN_DOES_NOT_CONSUME_ALL_THE_AMOUNT` errors.
 */
export class JupiterRoutePlanDoesNotConsumeAllTheAmountError extends JupiterApiError {
  public name = 'JupiterRoutePlanDoesNotConsumeAllTheAmountError';

  constructor(details: JupiterApiErrorDetails, context?: ErrorContext) {
    super(422, details, context);
  }
}

/**
 * `INVALID_REQUEST` errors.
 */
export class JupiterInvalidRequestError extends JupiterApiError {
  public name = 'JupiterInvalidRequestError';

  constructor(details: JupiterApiErrorDetails, context?: ErrorContext) {
    super(400, details, context);
  }
}

/**
 * `UNCATEGORIZED` errors.
 */
export class JupiterUncategorizedError extends JupiterApiError {
  public name = 'JupiterUncategorizedError';

  constructor(details: JupiterApiErrorDetails, context?: ErrorContext) {
    super(500, details, context);
  }
}

/**
 * Type for any constructor of a class that extends JupiterApiError.
 */
type JupiterApiErrorConstructor<T extends JupiterApiError> = new (
  details: JupiterApiErrorDetails,
  context?: ErrorContext,
) => T;

const TOKEN_NOT_TRADABLE = 'TOKEN_NOT_TRADABLE';
const NO_ROUTES_ERROR = 'NO_ROUTES_ERROR';
const COULD_NOT_FIND_ANY_ROUTE = 'COULD_NOT_FIND_ANY_ROUTE';
const FETCH_ROUTES_ERROR = 'FETCH_ROUTES_ERROR';
const ROUTE_PLAN_DOES_NOT_CONSUME_ALL_THE_AMOUNT = 'ROUTE_PLAN_DOES_NOT_CONSUME_ALL_THE_AMOUNT';
const INVALID_REQUEST = 'INVALID_REQUEST';
const UNCATEGORIZED = 'UNCATEGORIZED';

export const ROUTING_ERROR_CODES = [
  TOKEN_NOT_TRADABLE,
  NO_ROUTES_ERROR,
  COULD_NOT_FIND_ANY_ROUTE,
  FETCH_ROUTES_ERROR,
] as const;

/**
 * Map of error codes to error constructors.
 */
const codeToError: Record<string, JupiterApiErrorConstructor<JupiterApiError>> = {
  [TOKEN_NOT_TRADABLE]: JupiterTokenNotTradableError,
  [NO_ROUTES_ERROR]: JupiterFetchRoutesError,
  [COULD_NOT_FIND_ANY_ROUTE]: JupiterFetchRoutesError,
  [FETCH_ROUTES_ERROR]: JupiterFetchRoutesError,
  [ROUTE_PLAN_DOES_NOT_CONSUME_ALL_THE_AMOUNT]: JupiterRoutePlanDoesNotConsumeAllTheAmountError,
  [INVALID_REQUEST]: JupiterInvalidRequestError,
  [UNCATEGORIZED]: JupiterUncategorizedError,
};

/**
 * Create an instance of an API error based on the error key and details.
 * Defaults to uncategorized error if the error key is not recognized.
 */
export const createJupiterApiError = (
  details: JupiterApiErrorDetails,
  context?: ErrorContext,
): JupiterApiError =>
  new (codeToError[details.errorCode] ?? JupiterUncategorizedError)(details, context);
